import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useActiveOrg } from '../contexts/ActiveOrgContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import API from '../api';
import {
    LayoutDashboard,
    Bot,
    Briefcase,
    Settings,
    LogOut,
    Menu,
    X,
    User,
    Users,
    Building2,
    TrendingUp,
    Key,
    RefreshCw,
    FileSpreadsheet
} from 'lucide-react';

const DashboardLayout = () => {
    const { t } = useTranslation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const { activeOrg, hasMultipleOrgs, clearActiveOrg } = useActiveOrg();
    const [userProfile, setUserProfile] = useState({ first_name: '', last_name: '', email: '', username: '' });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await API.get(`/me/?_t=${Date.now()}`);
                setUserProfile(res.data);
            } catch (err) {
                console.error("Error fetching user profile:", err);
            }
        };
        fetchProfile();
    }, []);

    const handleLogout = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
                await API.post('/logout/', { refresh: refreshToken });
            }
        } catch (err) {
            console.error("Logout API error:", err);
        } finally {
            clearActiveOrg();
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('activeOrgId');
            localStorage.clear();
            navigate('/login');
        }
    };

    const handleSwitchOrg = () => {
        clearActiveOrg();
        navigate('/organizations');
    };

    // Org seçilmeden sadece Organizasyonlar sayfası erişilebilir
    const menuItems = activeOrg ? [
        { path: '/dashboard', icon: LayoutDashboard, label: t('common.dashboard') },
        { path: '/robots', icon: Bot, label: t('common.robots') },
        { path: '/jobs', icon: Briefcase, label: t('common.jobs') },
        ...(userProfile.is_superuser ? [{ path: '/organizations', icon: Building2, label: t('common.organizations') }] : []),
        { path: '/customers', icon: Users, label: t('common.customers') },
        { path: '/reports', icon: FileSpreadsheet, label: t('common.reports') },
        { path: '/vault', icon: Key, label: t('common.vault') },
        { path: '/settings', icon: Settings, label: t('common.settings') },
    ] : [
        { path: '/organizations', icon: Building2, label: t('common.organizations'), highlight: true },
    ];

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } lg:relative lg:translate-x-0`}
            >
                <div className="h-full flex flex-col">
                    {/* Logo */}
                    <Link to="/dashboard" className="h-24 flex flex-col items-center justify-center border-b border-gray-200 bg-white py-2 hover:bg-gray-50 transition-colors">
                        <img src="/logo.png" alt="Robopam Logo" className="w-12 h-12 object-contain" />
                        <span className="text-lg font-black text-blue-600 tracking-widest mt-1">ROBOPAM</span>
                    </Link>

                    {/* Active Organization */}
                    {activeOrg && (
                        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                            <Link to="/organizations" className="flex items-center hover:opacity-85 transition-opacity">
                                {activeOrg.logo ? (
                                    <img
                                        src={activeOrg.logo}
                                        alt={activeOrg.name}
                                        className="w-10 h-10 rounded-lg object-cover"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">
                                        {activeOrg.name?.charAt(0).toUpperCase() || 'O'}
                                    </div>
                                )}
                                <div className="ml-3 flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-blue-800 truncate">
                                        {activeOrg.name}
                                    </p>
                                    <p className="text-xs text-blue-600">{t('common.activeOrg')}</p>
                                </div>
                            </Link>
                            {hasMultipleOrgs && (
                                <button
                                    onClick={handleSwitchOrg}
                                    className="mt-2 w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                                >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    {t('common.switchOrg')}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isActive
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User & Logout */}
                    <div className="p-4 border-t border-gray-200">
                        {/* Language Switcher */}
                        <div className="mb-4 flex justify-center">
                            <LanguageSwitcher />
                        </div>

                        <div className="flex items-center mb-4 px-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                <User size={18} />
                            </div>
                            <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                    {userProfile.first_name ? `${userProfile.first_name} ${userProfile.last_name}` : (userProfile.username || t('common.loading'))}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{userProfile.email || '...'}</p>
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center px-4 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        >
                            <LogOut className="w-5 h-5 mr-3" />
                            {t('common.logout')}
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
                {/* Mobile Header */}
                <header className="lg:hidden h-16 bg-white border-b border-gray-200 flex items-center px-4 justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-85 transition-opacity">
                        <img src="/logo.png" alt="Robopam Logo" className="w-7 h-7 object-contain" />
                        <span className="text-lg font-bold text-blue-600 tracking-wider">ROBOPAM</span>
                        {activeOrg && (
                            <span className="ml-2 text-sm text-gray-500">| {activeOrg.name}</span>
                        )}
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher />
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
                        >
                            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </header>

                {/* Content Area */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
